import moment from 'moment';
import Alert from 'react-s-alert';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Button, Typography } from '@tempmee/react';
import { ListPageHeader } from '../../../commonComponents/ListPageHeader';
import { Spinner } from '../../../commonComponents/Spinner';
import { TabViewContainer } from '../../../commonComponents/TabViewContainer';
import { Table } from '../../../commonComponents/Table/Table';
import { RouteLink } from '../../../commonComponents/RouteLink';
import { style } from '../../../commonComponents/user-column';
import history from '../../../../history';
import { usePermanentJobList } from './hooks/usePermanentJobList';
import { Filters } from './components/Filters';
import { createLoadingSelector } from '../../../../apis/selectors';

const loadingSelector = createLoadingSelector(['GET_ALL_METADATA', 'GET_ALL_PROFESSIONS']);

/**
 * Where a listing row points. A draft has no posted job to show yet, so it
 * reopens in the post form; everything else opens the job itself. The row's
 * click handler and its View link both read this so they cannot disagree.
 */
const getPermanentJobUrl = (row) =>
  (row.status === 'draft'
    ? `/permanent-job/post?postingId=${row.id}`
    : `/permanent-job/office/${row.officeId}/job/${row.id}`);

export const PermanentJobListV2 = () => {
  const {
    data,
    isLoading,
    filters,
    total,
    totalPages,
    isError,
    error,
    handleFilterChanged,
    handlePageChanged,
    handleSortChanged,
  } = usePermanentJobList();

  const isInitialLoading = useSelector((state) => loadingSelector(state));

  useEffect(() => {
    if (isError) {
      Alert.error(error?.response?.data.error || error?.message, {
        position: 'bottom-right',
        effect: 'slide',
        beep: false,
        timeout: 5000,
        offset: 100,
      });
    }
  }, [isError, error]);

  if (isInitialLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '50px' }}>
        <Spinner />
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        height: '100%',
        overflowY: 'auto',
        flexDirection: 'column',
        position: 'relative',
        alignItems: 'center',
      }}
    >
      <TabViewContainer
        style={{
          marginInline: 'auto',
          width: '95%',
        }}
      >
        {/* Title lives on ListPageHeader now rather than TabViewContainer's own
            `title` prop, so this page matches the other admin list pages. */}
        <ListPageHeader
          title="Permanent Jobs"
          actions={(
            <Button onClick={() => history.push('/permanent-job/post')}>Post Permanent Job</Button>
          )}
          meta={(
            <Typography size="body_large" bold>
              Total | {total !== undefined ? Intl.NumberFormat().format(total) : ''}
            </Typography>
          )}
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <Filters filters={filters} handleFilterChanged={handleFilterChanged} />
        </div>
        <TabViewContainer style={{ margin: 0, padding: 20 }}>
          <Table
            filter={filters}
            columns={[
              {
                id: 'view',
                name: 'View',
                // An action cell rather than part of the row's click target.
                ignoreRowClick: true,
                width: '72px',
                cell: (row) => <RouteLink to={getPermanentJobUrl(row)}>View</RouteLink>,
                style: style.tableText,
              },
              {
                id: 'status',
                selector: ({ status }) => status,
                name: 'Status',
                width: '150px',
                style: { ...style.tableText, textTransform: 'capitalize' },
              },
              {
                id: 'Listing Name',
                selector: ({ title }) => title,
                name: 'Listing Name',
                style: { ...style.tableText },
              },
              {
                id: 'officeName',
                selector: ({ officeName }) => officeName,
                format: (row) => (
                  <RouteLink to={`/user/dentist/${row.officeId}`}>{row?.officeName}</RouteLink>
                ),
                name: 'Office Name',
                style: style.tableText,
              },
              {
                id: 'candidateCount',
                selector: ({ candidateCount }) => candidateCount || 0,
                name: 'Candidates',
                width: '120px',
                style: style.tableText,
              },
              {
                id: 'createdAt',
                selector: ({ createdAt }) =>
                  createdAt ? moment(createdAt).format('ddd, MMM Do, YYYY') : '-',
                name: 'Created',
                sortable: true,
                style: style.tableText,
              },
              {
                id: 'updatedAt',
                selector: ({ updatedAt }) =>
                  updatedAt ? moment(updatedAt).format('ddd, MMM Do, YYYY') : '-',
                name: 'Last Edited',
                sortable: true,
                style: style.tableText,
              },
              {
                id: 'planName',
                selector: ({ planName }) => planName || '--',
                name: 'Plan',
                width: '150px',
                style: style.tableText,
              },
              {
                id: 'recruiterEmail',
                selector: ({ recruiterEmail }) => recruiterEmail || '--',
                name: 'Recruiter',
                style: style.tableText,
              },
            ]}
            initialPage={filters.page}
            data={data}
            forceLoading={isLoading}
            noDataComponent="There are no Permanent Jobs"
            isLoading={isLoading}
            totalPages={totalPages}
            paginationPerPage={filters.limit}
            paginationTotalRows={total}
            onPageChange={handlePageChanged}
            onSort={handleSortChanged}
            onRowClicked={(row) => history.push(getPermanentJobUrl(row))}
            showColumnsToHide={false}
            showTotal={false}
            conditionalRowStyles={[
              {
                when: (row) => row.status === 'draft',
                style: {
                  backgroundColor: '#D9F2F3',
                },
              },
              {
                when: (row) => row.status === 'active',
                style: {
                  backgroundColor: '#99DAE3',
                },
              },
              {
                when: (row) => row.status === 'suspended',
                style: {
                  backgroundColor: '#FFDB9A',
                },
              },
              {
                when: (row) => row.status === 'closed',
                style: {
                  backgroundColor: '#AAB7C4',
                },
              },
            ]}
          />
        </TabViewContainer>
      </TabViewContainer>
    </div>
  );
};
